import { useGetAvailableAnalyticsApiV1, useGetWorkspacesApiV1Suspense } from "@/api/__generated__/api.schema";
import { ExternalLink } from "@/components/external-link";
import { WorkspaceIcon } from "@/components/workspace-icon";
import { externalUrls, imageUrls, routes } from "@/constants/url";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { useWorkspace } from "@/hooks/auth/use-workspace";
import { injectParams, useWorkspaceNavigate } from "@/lib/router/use-workspace-navigate";
import { WorkspaceLink } from "@/lib/router/workspace-link";
import { signout } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { UrlPattern } from "@/utils/url";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
  DropdownTrigger,
  Icon,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  Typography,
  useSidebar,
} from "@backpac/monica";
import { Children, isValidElement, useState } from "react";
import { Suspense } from "react";
import { useLocation, useNavigate } from "react-router";

export function AppSidebar() {
  const workspaceId = useWorkspaceId();
  const { data: available } = useGetAvailableAnalyticsApiV1(workspaceId);
  const IS_CUSTOM_MUSIC_AVAILABLE = available?.data.item ?? false;

  return (
    <Sidebar className="border-r border-borderLight">
      <SidebarContent className="flex flex-col gap-2">
        <WorkspaceLink to={routes.WORKSPACE_NAVIGATOR.root} className="flex items-center py-0.5 mb-2">
          <img src={imageUrls.logo.desktopBlack} alt="logo" width={126} height={28} />
        </WorkspaceLink>
        <SidebarGroup>
          <SidebarMenu>
            <AppSidebar.Item url={routes.PAYOUT.root} text="Payouts" icon="i-bp-sidebar-payout" />
          </SidebarMenu>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel className="ml-[-8px]">
            <Typography size="xs" color="textMuted" weight={500}>
              Products
            </Typography>
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <AppSidebar.Item url={routes.CAMPAIGNS.root} text="Campaigns" icon="i-bp-sidebar-announce" />
              {/* <AppSidebar.Item url={routes.MUSIC.root} text="Music" icon="i-bp-sidebar-music" /> */}
              {IS_CUSTOM_MUSIC_AVAILABLE ? (
                <AppSidebar.Group text="Custom Music" icon="i-bp-sidebar-music">
                  <AppSidebar.Item url={routes.CUSTOM_MUSIC.dspAnalytics} text="DSP Analytics" />
                </AppSidebar.Group>
              ) : null}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel className="ml-[-8px]">
            <Typography size="xs" color="textMuted" weight={500}>
              Creator Hub
            </Typography>
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <AppSidebar.Item url={routes.CHANNELS.root} text="Social Accounts" icon="i-bp-sidebar-video" />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu className="flex flex-col gap-1">
          <AppSidebar.ExternalItem href={externalUrls.HELP_CENTER_ROOT_URL} text="Help Center" icon="i-bp-help" />
          <AppSidebar.WorkspaceItem />
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

interface SidebarItemProps {
  url: string;
  text: string;
  icon?: string;
}

AppSidebar.Item = function AddSidebarItem({ url, text, icon }: SidebarItemProps) {
  const { setOpenMobile } = useSidebar();
  const currentPath = useLocation().pathname;
  const isActive = new UrlPattern(url).contains(currentPath) !== null;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        size="sm"
        onClick={() => {
          setOpenMobile(false);
        }}
        isActive={isActive}
      >
        <WorkspaceLink to={url} className={cn("w-full flex items-center gap-3 h-full")}>
          <div className="flex items-center justify-center">
            <i className={cn("inline-block size-4", icon)} />
          </div>
          <Typography weight={500} size="xs" color="textPrimary">
            {text}
          </Typography>
        </WorkspaceLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

interface SidebarExternalItemProps {
  href: string;
  text: string;
  icon?: string;
}

AppSidebar.ExternalItem = function AddSidebarExternalItem({ href, text, icon }: SidebarExternalItemProps) {
  const { setOpenMobile } = useSidebar();
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        size="sm"
        onClick={() => {
          setOpenMobile(false);
        }}
      >
        <ExternalLink to={href} className={cn("w-full flex items-center gap-3 h-full")}>
          <div className="flex items-center justify-center">
            <i className={cn("inline-block size-4", icon)} />
          </div>
          <Typography weight={500} size="xs" color="textPrimary">
            {text}
          </Typography>
        </ExternalLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

interface SidebarGroupProps {
  text: string;
  icon: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

AppSidebar.Group = function SidebarGroup({ text, icon, children, defaultOpen }: SidebarGroupProps) {
  const currentPath = useLocation().pathname;
  const isActive = Children.toArray(children).some((child) => {
    if (isValidElement(child) && "props" in child) {
      const url = (child.props as { url: string }).url;
      return currentPath.startsWith(url);
    }
    return false;
  });

  return (
    <Collapsible defaultOpen={defaultOpen ?? isActive}>
      <CollapsibleTrigger className="w-full" asChild>
        {(open) => (
          <SidebarMenuButton size="sm" className="w-full flex items-center justify-between h-9">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center">
                <i className={cn("inline-block size-4", icon)} />
              </div>
              <Typography weight={500} size="xs" color="textPrimary">
                {text}
              </Typography>
            </div>
            <Icon
              name="chevron-down"
              iconSize="16px"
              className={cn("transition-transform duration-200", open && "rotate-180")}
            />
          </SidebarMenuButton>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent>{children}</CollapsibleContent>
    </Collapsible>
  );
};

AppSidebar.WorkspaceItem = function AppSidebarWorkspaceItem() {
  const navigate = useWorkspaceNavigate();
  const { data } = useWorkspace();
  const { setOpenMobile } = useSidebar();

  return (
    <Dropdown>
      <DropdownTrigger className="w-full justify-start py-1.5 hover:bg-backgroundLight active:bg-backgroundLight rounded-[8px] focus:outline-none">
        <WorkspaceTitle className="justify-start px-[10px]" title={data.name} thumbnailImgSrc={data.thumbnailUrl} />
      </DropdownTrigger>
      <DropdownContent className="border border-borderLight p-1 min-w-[200px]" align="start">
        <DropdownItem
          className="p-2 m-1"
          onClick={() => {
            navigate(routes.SETTINGS.workspace);
            setOpenMobile(false);
          }}
        >
          <i className="inline-block i-bp-sidebar-setting size-4 text-textSubtle" />
          <Typography weight={500} size="xs" color="textPrimary">
            Workspace Settings
          </Typography>
        </DropdownItem>
        <DropdownItem
          className="p-2 m-1"
          onClick={() => {
            navigate(routes.SETTINGS.account);
            setOpenMobile(false);
          }}
        >
          <i className="inline-block i-bp-user size-4 text-textSubtle" />
          <Typography weight={500} size="xs" color="textPrimary">
            Account
          </Typography>
        </DropdownItem>
        <DropdownSeparator />
        <Suspense fallback={null}>
          <WorkspaceSwitcher className="pr-1 m-1" />
        </Suspense>
        <DropdownItem className="p-2 m-1" onClick={() => signout()}>
          <i className="inline-block i-bp-sidebar-signout size-4" />
          <Typography weight={500} size="xs" color="textPrimary">
            Sign out
          </Typography>
        </DropdownItem>
      </DropdownContent>
    </Dropdown>
  );
};

function WorkspaceSwitcher({ className }: { className?: string }) {
  const [collapsed, setCollapsed] = useState(false);
  const { setOpenMobile } = useSidebar();
  const { data: myWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const {
    data: {
      data: { items: workspaces },
    },
  } = useGetWorkspacesApiV1Suspense();

  if (workspaces.filter((workspace) => workspace.id !== myWorkspace.id).length === 0) {
    return null;
  }

  return (
    <Collapsible
      className={cn(className, "min-w-[200px] w-full")}
      open={collapsed}
      onOpenChange={(open) => {
        setCollapsed(open);
      }}
    >
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center justify-between p-2 hover:bg-backgroundLight rounded-[8px] w-full">
          <div className="flex items-center gap-2">
            <i className="inline-block i-bp-swap size-4" />
            <Typography weight={500} size="xs" color="textPrimary">
              Switch workspace
            </Typography>
          </div>
          <i
            className={cn(
              "inline-flex i-bp-chevron-down size-3 pr-2 text-textSubtle transition-transform",
              !collapsed && "rotate-[-90deg]",
            )}
          />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-1 pl-2 pr-1 w-full">
        {workspaces.map((workspace) => (
          <DropdownItem
            key={workspace.id}
            className="pl-2 pr-1 py-1.5 w-full"
            onClick={() => {
              navigate(injectParams(routes.HOME.root, { workspaceId: workspace.id }));
              setOpenMobile(false);
            }}
          >
            <div className="flex items-center justify-between w-full">
              <WorkspaceTitle title={workspace.name} thumbnailImgSrc={workspace.thumbnailUrl} />
              {workspace.id === myWorkspace.id ? <i className="inline-block i-bp-sidebar-checkbox size-3 " /> : null}
            </div>
          </DropdownItem>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

function WorkspaceTitle({
  className,
  title,
  thumbnailImgSrc,
}: { className?: string; title: string; thumbnailImgSrc?: string }) {
  return (
    <div className={cn("flex items-center justify-center gap-2", className)}>
      <WorkspaceIcon workspaceName={title} thumbnailUrl={thumbnailImgSrc} />
      <Typography weight={500} size="xs" color="textPrimary">
        {title}
      </Typography>
    </div>
  );
}

interface AppSidebarAlertProps {
  title: string;
  description: string;
}

AppSidebar.Alert = function AppSidebarAlert({ title, description }: AppSidebarAlertProps) {
  const [isOpen, setIsOpen] = useState(true);

  if (isOpen === false) {
    return null;
  }

  return (
    <div className="space-y-2 rounded-[12px] border border-colorOrange bg-colorOrangeLight p-4">
      <div className="flex items-center justify-between gap-4">
        <Typography size="xs" color="textPrimary" weight={600}>
          {title}
        </Typography>
        <button type="button" className="p-0" onClick={() => setIsOpen(false)}>
          <i className="inline-block i-bp-close size-4" />
        </button>
      </div>
      <Typography size="paragraphXs" color="textMuted">
        {description}
      </Typography>
    </div>
  );
};
